'use client';

import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { motion } from 'framer-motion';
import { FaCircle, FaEdit, FaLock, FaLockOpen, FaSave, FaTimes } from 'react-icons/fa';
import { DayData } from '../../../../services/calendarService';
import { toast } from 'react-hot-toast';
import { useCalendar } from '../../../../context/CalendarContext';
import { formatDate, getDaysInMonth, withTimeout } from '../../../../utils/calendarUtils';

interface CalendarGridProps {
  currentDate: Date;
  onDataChange?: () => void;
}

// Type for calendar day with date as key
type CalendarData = Record<string, DayData>;

// Type for edit form
interface EditFormData {
  available: boolean;
  price: number;
}

// Individual day cell component (memoized for performance)
const DayCell = memo(({ 
  day, 
  dayData, 
  isSelected, 
  onSelect 
}: { 
  day: number; 
  dayData: DayData | undefined; 
  isSelected: boolean; 
  onSelect: () => void;
}) => {
  // Skip rendering for empty cells
  if (!dayData) return <div className="h-24 bg-gray-50"></div>;
  
  const { date, available, price, bookingId, guestName } = dayData;
  
  return (
    <motion.div 
      whileHover={{ scale: 1.02 }}
      className={`
        h-24 p-2 border rounded-md cursor-pointer relative
        ${isSelected ? 'border-blue-500 shadow-md' : 'border-gray-200'}
        ${available ? 'bg-white' : 'bg-gray-100'}
        ${bookingId ? 'bg-green-50 border-green-200' : ''}
      `}
      onClick={onSelect}
    >
      <div className="flex justify-between items-start">
        <span className="font-medium">{day}</span>
        {!available && (
          <span className="text-red-500">
            <FaLock size={12} />
          </span>
        )}
      </div>
      
      {bookingId ? (
        <div className="mt-1 text-xs">
          <div className="flex items-center text-green-700">
            <FaCircle className="mr-1" size={8} />
            <span className="font-medium">Booked</span>
          </div>
          {guestName && (
            <div className="mt-1 truncate">{guestName}</div>
          )}
        </div>
      ) : (
        <div className="mt-2 text-sm font-medium">
          ${price}
        </div>
      )}
    </motion.div>
  );
});

DayCell.displayName = 'DayCell';

export default function CalendarGrid({ currentDate, onDataChange }: CalendarGridProps) {
  // Get calendar context
  const { 
    calendarData: contextCalendarData,
    updateDayData,
    isLoading: contextIsLoading,
    errors
  } = useCalendar();
  
  // Local state
  const [calendarData, setCalendarData] = useState<CalendarData>({});
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editingDay, setEditingDay] = useState<DayData | null>(null);
  const [editForm, setEditForm] = useState<EditFormData>({ available: true, price: 0 });
  const [localLoading, setLocalLoading] = useState<boolean>(false);
  
  // Refs
  const isMountedRef = useRef<boolean>(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  // Update local calendar data when context data changes
  useEffect(() => {
    if (Object.keys(contextCalendarData).length > 0) {
      // Filter data for current month only
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const monthPrefix = `${year}-${String(month + 1).padStart(2, '0')}`;
      
      const filteredData: CalendarData = {};
      Object.entries(contextCalendarData).forEach(([date, data]) => {
        if (date.startsWith(monthPrefix)) {
          filteredData[date] = data;
        }
      });
      
      setCalendarData(filteredData);
    }
  }, [contextCalendarData, currentDate]);
  
  // Handle day selection
  const handleSelectDay = useCallback((date: string) => {
    setSelectedDate(date);
    setEditMode(false);
    
    const dayData = calendarData[date];
    if (dayData) {
      setEditingDay(dayData);
      setEditForm({
        available: dayData.available,
        price: dayData.price
      });
    }
  }, [calendarData]);
  
  // Handle edit mode toggle
  const handleEditToggle = useCallback(() => {
    setEditMode(prev => !prev);
  }, []);
  
  // Handle form changes
  const handleFormChange = useCallback((field: keyof EditFormData, value: any) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);
  
  // Handle save changes
  const handleSaveChanges = useCallback(async () => {
    if (!editingDay) return;
    
    try {
      setLocalLoading(true);
      
      // Create updated day data
      const updatedDayData: DayData = {
        ...editingDay,
        available: editForm.available,
        price: editForm.price
      };
      
      // Update day data with timeout - this will trigger the notifyListeners('day-updated') in the context
      await withTimeout(
        updateDayData(updatedDayData),
        5000,
        'Update day data timed out'
      );
      
      // Update local state
      setCalendarData(prev => ({
        ...prev,
        [editingDay.date]: updatedDayData
      }));
      
      setEditingDay(updatedDayData);
      setEditMode(false);
      
      // Call onDataChange callback to ensure parent components are notified
      if (onDataChange) {
        onDataChange();
      }
      
      toast.success('Day updated successfully');
    } catch (error) {
      console.error('Error updating day:', error);
      toast.error('Failed to update day');
    } finally {
      setLocalLoading(false);
    }
  }, [editingDay, editForm, updateDayData, onDataChange]);
  
  // Cancel editing
  const handleCancelEdit = useCallback(() => {
    setEditMode(false);
    if (editingDay) {
      setEditForm({
        available: editingDay.available,
        price: editingDay.price
      });
    }
  }, [editingDay]);
  
  // Generate calendar grid
  const renderCalendarGrid = useCallback(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfMonth = new Date(year, month, 1).getDay(); // 0 = Sunday
    
    // Create grid cells
    const cells = [];
    
    // Add empty cells for days before the first day of month
    for (let i = 0; i < firstDayOfMonth; i++) {
      cells.push(
        <div key={`empty-${i}`} className="h-24 bg-gray-50"></div>
      );
    }
    
    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = formatDate(date);
      const dayData = calendarData[dateStr];
      const isSelected = selectedDate === dateStr;
      
      cells.push(
        <DayCell
          key={dateStr}
          day={day}
          dayData={dayData}
          isSelected={isSelected}
          onSelect={() => handleSelectDay(dateStr)}
        />
      );
    }
    
    return cells;
  }, [currentDate, calendarData, selectedDate, handleSelectDay]);
  
  // Render day details panel
  const renderDayDetails = useCallback(() => {
    if (!selectedDate || !editingDay) return null;
    
    const date = new Date(selectedDate);
    const formattedDate = date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    return (
      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">{formattedDate}</h3>
          
          {!editingDay.bookingId && (
            <button
              onClick={handleEditToggle}
              className="text-blue-600 hover:text-blue-800"
              disabled={localLoading}
            >
              {editMode ? (
                <FaTimes className="h-5 w-5" />
              ) : (
                <FaEdit className="h-5 w-5" />
              )}
            </button>
          )}
        </div>
        
        {editingDay.bookingId ? (
          <div className="space-y-2">
            <div className="flex items-center text-green-700">
              <FaCircle className="mr-2" size={10} />
              <span className="font-medium">Booked</span>
            </div>
            {editingDay.guestName && (
              <p className="text-gray-700">
                <span className="font-medium">Guest:</span> {editingDay.guestName}
              </p>
            )}
            <p className="text-gray-700">
              <span className="font-medium">Price:</span> ${editingDay.price}
            </p>
          </div>
        ) : editMode ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Availability
              </label>
              <div className="flex items-center space-x-4">
                <button
                  type="button"
                  onClick={() => handleFormChange('available', true)}
                  className={`px-3 py-2 rounded-md flex items-center ${editForm.available ? 'bg-blue-100 text-blue-800 border-blue-300 border' : 'bg-gray-100 text-gray-700'}`}
                >
                  <FaLockOpen className="mr-2" size={14} />
                  Available
                </button>
                <button
                  type="button"
                  onClick={() => handleFormChange('available', false)}
                  className={`px-3 py-2 rounded-md flex items-center ${!editForm.available ? 'bg-blue-100 text-blue-800 border-blue-300 border' : 'bg-gray-100 text-gray-700'}`}
                >
                  <FaLock className="mr-2" size={14} />
                  Blocked
                </button>
              </div>
            </div>
            
            <div>
              <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
                Price
              </label>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <input
                  type="number"
                  name="price"
                  id="price"
                  className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                  placeholder="0.00"
                  value={editForm.price}
                  onChange={(e) => handleFormChange('price', parseFloat(e.target.value) || 0)}
                  disabled={!editForm.available || localLoading}
                />
              </div>
            </div>
            
            <div className="flex space-x-2 pt-2">
              <button
                type="button"
                onClick={handleSaveChanges}
                disabled={localLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {localLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave className="mr-2" size={14} />
                    Save Changes
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleCancelEdit}
                disabled={localLoading}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-gray-700">
              <span className="font-medium">Status:</span> {editingDay.available ? 'Available' : 'Blocked'}
            </p>
            <p className="text-gray-700">
              <span className="font-medium">Price:</span> ${editingDay.price}
            </p>
          </div>
        )}
      </div>
    );
  }, [selectedDate, editingDay, editMode, editForm, localLoading, handleEditToggle, handleFormChange, handleSaveChanges, handleCancelEdit]);
  
  return (
    <div className="h-full relative">
      {/* Loading overlay */}
      {(contextIsLoading || localLoading) && (
        <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-10">
          <div className="flex flex-col items-center">
            <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="mt-2 text-sm text-gray-600">Loading calendar data...</p>
          </div>
        </div>
      )}
      
      {/* Error display */}
      {errors.calendar && (
        <div className="bg-red-50 p-4 rounded-md mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading calendar</h3>
              <p className="text-sm text-red-700 mt-1">{errors.calendar instanceof Error ? errors.calendar.message : String(errors.calendar)}</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Calendar grid */}
        <div className="lg:col-span-2">
          <div className="grid grid-cols-7 gap-2">
            {/* Weekday headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className="text-center font-medium text-gray-700 py-2">
                {day}
              </div>
            ))}
            
            {/* Calendar cells */}
            {renderCalendarGrid()}
          </div>
        </div>
        
        {/* Day details panel */}
        <div className="lg:col-span-1">
          {selectedDate ? (
            renderDayDetails()
          ) : (
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-center text-gray-500">
              Select a day to view details
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
